/**
 * PrepMasters — Unified In-App Purchase Bridge (multi-tier + per-course)
 *
 * Wraps `cordova-plugin-purchase` (v13). Registers ALL tier products AND
 * every course-level per-course product ID (fetched from GET /courses at
 * init time) so any course can be purchased whether it uses a shared
 * tier product or its own non-renewing subscription.
 *
 * Wire-up:
 *   • `IAP.init()` once after login (from CapacitorBootstrap).
 *   • `IAP.buy({ courseId, productId })` from the Subscribe button.
 *   • `verified` callback POSTs receipt + course_id to the backend, which
 *     validates the product matches the course, then unlocks that course
 *     for 6 months.
 */
import { isNativeIOS, isNativeAndroid, isNativeApp } from "./platform";
import { api } from "./api";
import { TIER_CATALOG, TIER_BY_KEY, TIER_BY_PRODUCT } from "./pricing";

const _state = {
  initialized: false,
  store: null,
  courseIdInFlight: null,   // set right before .order() so the verify event can attach the enrollment
  productIdInFlight: null,  // which tier product was actually purchased (needed on Android)
  registeredIds: new Set(), // product IDs already registered on the store
};

function _getStore() {
  if (typeof window === "undefined") return null;
  const CdvPurchase = window.CdvPurchase;
  if (!CdvPurchase) return null;
  return CdvPurchase.store;
}

async function _fetchPublicProductIds() {
  // Public endpoint (no login needed) — init() runs at app launch, before login.
  try {
    const res = await api.get("/iap/product-ids");
    return Array.isArray(res?.data?.product_ids) ? res.data.product_ids : [];
  } catch (e) {
    console.warn("[IAP] Could not fetch public product ids:", e?.message || e);
    return [];
  }
}

async function _fetchCourseProductIds() {
  // Fetch the current user's course list and extract every per-course
  // `iap_product_id` (exposed by the backend as `pricing.product_id`).
  // Silently returns [] on failure so IAP.init() never blocks the app.
  try {
    const res = await api.get("/courses");
    const rows = Array.isArray(res?.data) ? res.data : [];
    const ids = new Set();
    for (const c of rows) {
      const pid = c?.pricing?.product_id;
      if (pid) ids.add(pid);
    }
    return Array.from(ids);
  } catch (e) {
    console.warn("[IAP] Could not fetch /courses for product registration:", e?.message || e);
    return [];
  }
}

/** Register all tier + per-course products with the store. Safe to call multiple times. */
export async function init() {
  if (_state.initialized) return;
  if (!isNativeApp()) return;

  const store = _getStore();
  if (!store) {
    console.warn("[IAP] CdvPurchase plugin not present; skipping init.");
    return;
  }

  const CdvPurchase = window.CdvPurchase;
  const { ProductType, Platform } = CdvPurchase;
  const platform = isNativeIOS() ? Platform.APPLE_APPSTORE : Platform.GOOGLE_PLAY;
  // iOS: Non-Renewing Subscription (matches App Store Connect product type —
  //   manual re-purchase every 6 months, aligns with current submission).
  // Android: Google Play Subscription (auto-renewing every 6 months).
  //   Backend verifies via purchases.subscriptionsv2.get, uses Google's
  //   `expiryTime` as source of truth so renewals extend access automatically.
  const productType = isNativeIOS()
    ? ProductType.NON_RENEWING_SUBSCRIPTION
    : ProductType.PAID_SUBSCRIPTION;

  // Union of: 4 shared tier products + every course's own iap_product_id.
  const tierIds = TIER_CATALOG.map((t) => t.product_id);
  const [publicIds, courseIds] = await Promise.all([_fetchPublicProductIds(), _fetchCourseProductIds()]);
  const allIds = Array.from(new Set([...tierIds, ...publicIds, ...courseIds]));

  store.register(allIds.map((id) => ({ id, type: productType, platform })));
  allIds.forEach((id) => _state.registeredIds.add(id));

  store.when()
    .approved(async (transaction) => {
      const pending = _state.pending;
      try {
        await _verifyOnBackend(transaction);
        // Server confirmed + granted access -> tell Apple/Google we delivered it.
        await transaction.finish();
        _forgetPending(transaction.products?.[0]?.id);
        pending?.resolve({ ok: true });
      } catch (err) {
        console.error("[IAP] Backend verify failed", err);
        pending?.reject(new Error(_errMsg(err, "Payment received, but we couldn't activate the course yet. Tap Restore Purchases, or contact support.")));
      } finally {
        if (_state.pending === pending) _state.pending = null;
      }
    })
    .productUpdated((product) => {
      console.debug("[IAP] product updated", product.id, product.pricing?.price);
    });

  await store.initialize([platform]);
  _state.store = store;
  _state.initialized = true;
}

const PENDING_KEY = "pm_iap_pending_courses";
function _pendingMap() {
  try { return JSON.parse(localStorage.getItem(PENDING_KEY) || "{}"); } catch { return {}; }
}
function _rememberPending(productId, courseId) {
  const m = _pendingMap(); m[productId] = courseId; localStorage.setItem(PENDING_KEY, JSON.stringify(m));
}
function _forgetPending(productId) {
  const m = _pendingMap(); delete m[productId]; localStorage.setItem(PENDING_KEY, JSON.stringify(m));
}
function _errMsg(err, fallback) {
  return err?.response?.data?.detail || err?.message || fallback;
}

async function _appleReceipt(transaction, forceRefresh = false) {
  const read = () =>
    transaction?.parentReceipt?.nativeData?.appStoreReceipt ||
    _state.store?.localReceipts?.find((r) => r.platform === "ios-appstore")?.nativeData?.appStoreReceipt;
  let data = forceRefresh ? null : read();
  if (!data) {
    try { await _state.store.getAdapter("ios-appstore")?.refreshReceipt(); } catch (e) { console.warn("[IAP] refreshReceipt failed", e); }
    data = read();
  }
  if (!data) throw new Error("Could not read the App Store receipt. Please tap Restore Purchases.");
  return data;
}

async function _verifyOnBackend(transaction) {
  const platform = transaction.platform;
  const productId = transaction.products?.[0]?.id || _state.productIdInFlight;
  // Course for this purchase: the one in flight, or remembered (app was closed mid-purchase).
  // If neither is known (older purchase re-delivered by Apple/Google), the server
  // finds the course from the product ID.
  const courseId = _state.courseIdInFlight || _pendingMap()[productId] || undefined;

  if (platform === "ios-appstore") {
    let receiptData = await _appleReceipt(transaction);
    try {
      await api.post("/iap/apple/verify-receipt", { receipt_data: receiptData, course_id: courseId, product_id: productId });
    } catch (e) {
      // Cached receipt may predate this purchase — refresh once and retry.
      if (e?.response?.status !== 400) throw e;
      receiptData = await _appleReceipt(transaction, true);
      await api.post("/iap/apple/verify-receipt", { receipt_data: receiptData, course_id: courseId, product_id: productId });
    }
  } else if (platform === "android-playstore") {
    await api.post("/iap/google/verify-purchase", {
      purchase_token: transaction.transactionId,
      product_id: transaction.products?.[0]?.id || _state.productIdInFlight,
      course_id: courseId,
    });
  }
}

/**
 * Return metadata for a specific tier product, or `null` if unavailable.
 * Callers typically pass `course.pricing.product_id`.
 */
export function getProduct(productId) {
  if (!_state.store) return null;
  if (!productId) return null;
  const product = _state.store.get(productId);
  if (!product) return null;
  return {
    id: product.id,
    title: product.title,
    description: product.description,
    price: product.pricing?.price || null,
    priceMicros: product.pricing?.priceMicros || null,
    currency: product.pricing?.currency || null,
    canPurchase: product.canPurchase,
  };
}

/**
 * Kick off the purchase flow for a specific course.
 *
 * @param {object} args
 * @param {string} args.courseId    — the DB id of the course being unlocked
 * @param {string} [args.productId] — tier product ID from course.pricing.product_id
 * @param {string} [args.tier]      — tier key ("tier_1"…"tier_4") as an alternative
 */
export async function buy(args) {
  // Legacy call signature: buy(courseId) → default to legacy single product.
  if (typeof args === "string") {
    args = { courseId: args };
  }
  const { courseId } = args || {};
  let { productId, tier } = args || {};
  if (!isNativeApp()) {
    throw new Error("IAP.buy() called outside of a native app runtime.");
  }
  if (!courseId) throw new Error("courseId is required.");
  if (!productId && tier) productId = TIER_BY_KEY[tier]?.product_id;
  if (!productId) throw new Error("productId or tier is required.");
  if (!_state.initialized) await init();
  const store = _state.store;
  if (!store) throw new Error("IAP plugin is not available.");

  let product = store.get(productId);
  if (!product) {
    await ensureProducts([productId]);
    for (let i = 0; i < 10 && !product; i++) {
      await new Promise((r) => setTimeout(r, 500));
      product = store.get(productId);
    }
  }
  if (!product) throw new Error("The App Store is still loading this subscription. Please try again in a moment.");
  const offer = product.getOffer();
  if (!offer) throw new Error("No offer available on the store product.");

  _state.courseIdInFlight = courseId;
  _state.productIdInFlight = productId;
  _rememberPending(productId, courseId);
  // Resolves only after OUR server verified the receipt and unlocked the course.
  const done = new Promise((resolve, reject) => { _state.pending = { resolve, reject }; });
  const err = await offer.order();
  if (err) {
    _state.pending = null;
    if (err.code === window.CdvPurchase?.ErrorCode?.PAYMENT_CANCELLED) throw new Error("Purchase cancelled.");
    throw new Error(err.message || "The purchase could not be completed.");
  }
  const timeout = new Promise((_, reject) =>
    setTimeout(() => reject(new Error("Still confirming your purchase. Pull to refresh in a minute, or tap Restore Purchases.")), 120000));
  return Promise.race([done, timeout]);
}

/** Register any product IDs not yet known to the store and refresh from Apple/Google. */
export async function ensureProducts(ids) {
  if (!isNativeApp()) return;
  if (!_state.initialized) await init();
  const store = _state.store;
  if (!store) return;
  const missing = (ids || []).filter((id) => id && !_state.registeredIds.has(id));
  if (missing.length) {
    const { ProductType, Platform } = window.CdvPurchase;
    const platform = isNativeIOS() ? Platform.APPLE_APPSTORE : Platform.GOOGLE_PLAY;
    const type = isNativeIOS() ? ProductType.NON_RENEWING_SUBSCRIPTION : ProductType.PAID_SUBSCRIPTION;
    store.register(missing.map((id) => ({ id, type, platform })));
    missing.forEach((id) => _state.registeredIds.add(id));
  }
  if (missing.length || (ids || []).some((id) => !store.get(id))) {
    try { await store.update(); } catch (e) { console.warn("[IAP] store.update failed", e); }
  }
}

/** Restore purchases (Apple mandates a visible "Restore Purchases" button). */
export async function restore({ courseId } = {}) {
  if (!isNativeApp()) return;
  if (!_state.initialized) await init();
  if (!_state.store) return;
  if (isNativeIOS()) {
    // Non-renewing subscriptions are not "restored" by StoreKit — they live in the
    // app receipt, so refresh it and ask our server to re-check this course.
    if (!courseId) throw new Error("Select a course to restore.");
    const receiptData = await _appleReceipt(null, true);
    await api.post("/iap/apple/verify-receipt", { receipt_data: receiptData, course_id: courseId });
    return;
  }
  await _state.store.restorePurchases();
}

const IAP = { init, buy, restore, getProduct, ensureProducts, TIER_CATALOG, TIER_BY_KEY, TIER_BY_PRODUCT };
export default IAP;
